import React from 'react';
import { stripHtmlToText } from './htmlText';
import { AttachmentImage } from './AttachmentImage';
import { FeedbackDetail } from './types';
import { buildHighlightUrl, extractTargets } from './highlight';

// ---------- 상세 오버레이 ----------
export const DetailOverlay: React.FC<{ loading: boolean; detail: FeedbackDetail | null; onClose: () => void }> = ({ loading, detail, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'absolute',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      zIndex: 10,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%',
        maxHeight: '85%',
        background: 'white',
        borderRadius: '10px 10px 0 0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #eee' }}>
        <strong style={{ fontSize: 13 }}>피드백 #{detail?.id ?? '…'}</strong>
        <div style={{ flex: 1 }} />
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 16,
            cursor: 'pointer',
            color: '#666',
          }}
        >
          ×
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {loading && <div style={{ color: '#888', fontSize: 12 }}>로딩…</div>}
        {detail && <DetailBody detail={detail} />}
      </div>
    </div>
  </div>
);

/** 글의 화면을 새 탭으로 열고 지정 요소를 박스로 표시 (FeedbackHighlighter). */
export const ViewOnPageButton: React.FC<{ detail: FeedbackDetail }> = ({ detail }) => {
  const url = buildHighlightUrl(detail);
  if (!url) return null;
  const n = extractTargets(detail).length;
  return (
    <button
      type="button"
      data-testid="btn-dev-feedback-view-on-page"
      onClick={() => window.open(url, '_blank', 'noopener')}
      style={{ marginTop: 6, padding: '5px 12px', borderRadius: 6, border: '1px solid #F97316', background: '#FFF7ED',
        color: '#9A3412', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
    >
      해당 화면에서 보기{n > 0 ? ` · 표시 ${n}곳` : ''}
    </button>
  );
};

const DetailBody: React.FC<{ detail: FeedbackDetail }> = ({ detail }) => {
  let log: Array<{ kind: string; content: string; ts?: string; pageUrl?: string; pageTitle?: string }> = [];
  try {
    log = JSON.parse(detail.chatLog);
  } catch {
    log = [];
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
      <div style={{ color: '#666' }}>
        <div>페이지: {detail.pageTitle} <span style={{ color: '#aaa' }}>{detail.pageUrl}</span></div>
        <ViewOnPageButton detail={detail} />
        <div>신고자: {detail.reporter || '-'} · 환경: {detail.env}</div>
        <div>등록: {new Date(detail.createdAt).toLocaleString()}</div>
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '4px 0' }} />
      {log.map((m, i) => (
        <div
          key={i}
          style={{
            padding: '6px 8px',
            background: '#f7f7f8',
            borderRadius: 6,
            wordBreak: 'break-all',
          }}
        >
          {m.kind === 'text' && (
            /^\s*<[a-zA-Z][\w-]*/.test(m.content) || /<\/[a-zA-Z][\w-]*>/.test(m.content) ? (
              <pre
                style={{
                  fontSize: 11, background: '#0f172a', color: '#f1f5f9',
                  padding: '6px 8px', borderRadius: 4, margin: 0,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowX: 'auto',
                }}
              ><code>{m.content}</code></pre>
            ) : (
              <span style={{ whiteSpace: 'pre-wrap' }}>{stripHtmlToText(m.content)}</span>
            )
          )}
          {m.kind === 'id' && (
            <span
              style={{
                display: 'inline-block',
                background: '#fff1eb',
                color: '#e94d20',
                border: '1px solid #ffcbb4',
                borderRadius: 12,
                padding: '2px 8px',
                fontFamily: 'ui-monospace, monospace',
              }}
            >
              #{m.content}
            </span>
          )}
          {m.kind === 'image_ref' && m.content.startsWith('attachment:') && (
            <AttachmentImage
              attachmentId={Number(m.content.slice('attachment:'.length))}
              alt="첨부 이미지 (클릭하면 원본)"
              style={{ maxWidth: 220, maxHeight: 220, display: 'block', borderRadius: 4, cursor: 'zoom-in' }}
            />
          )}
          {m.pageUrl && (
            <div style={{ fontSize: 10, color: '#aaa', marginTop: 2 }}>
              {m.pageTitle || m.pageUrl}
            </div>
          )}
        </div>
      ))}
      {detail.attachments && detail.attachments.length > 0 && log.every((m) => m.kind !== 'image_ref') && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {detail.attachments.map((a) => (
            <AttachmentImage
              key={a.id}
              attachmentId={a.id}
              alt={a.fileName}
              title={`원본: ${a.fileName}`}
              style={{ maxWidth: 120, maxHeight: 120, borderRadius: 4, border: '1px solid #eee', cursor: 'zoom-in' }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
